import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, Search, X, Loader2 } from "lucide-react";

// options: [{ value, label, sublabel? }]. Searches label + sublabel client-side.
// For large datasets, pass `onSearch(query) => Promise<options[]>` to search server-side,
// and `selectedOption` so the current value's label renders even when not in the fetched page.
export function SearchableSelect({
  options = [], value, onChange, placeholder = "Select…",
  searchPlaceholder = "Search…", testid = "searchable-select", clearable = false,
  onSearch = null, selectedOption = null,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [remote, setRemote] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Server-side search: debounced fetch whenever the query changes while open.
  useEffect(() => {
    if (!onSearch || !open) return;
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try { const res = await onSearch(q.trim()); if (active) setRemote(res || []); }
      catch { if (active) setRemote([]); }
      finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, open, onSearch]);

  const query = q.trim().toLowerCase();
  const list = onSearch
    ? remote
    : (query ? options.filter((o) => `${o.label} ${o.sublabel || ""}`.toLowerCase().includes(query)) : options);

  const selected =
    (picked && picked.value === value && picked)
    || (selectedOption && selectedOption.value === value && selectedOption)
    || options.find((o) => o.value === value)
    || remote.find((o) => o.value === value)
    || null;

  const clear = () => { onChange("", null); setPicked(null); };

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen((v) => !v)}
        className="w-full border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical flex items-center justify-between gap-2 text-left"
      >
        <span className={selected ? "text-ink truncate" : "text-muted2"}>
          {selected ? (
            <>{selected.label}{selected.sublabel ? <span className="text-muted2"> — {selected.sublabel}</span> : null}</>
          ) : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {clearable && selected && (
            <X
              className="w-4 h-4 text-muted2 hover:text-ink"
              data-testid={`${testid}-clear`}
              onClick={(e) => { e.stopPropagation(); clear(); }}
            />
          )}
          <ChevronDown className="w-4 h-4 text-muted2" />
        </span>
      </button>

      {open && (
        <div data-testid={`${testid}-dropdown`} className="absolute z-40 mt-1 w-full bg-white border border-line rounded-md shadow-lg">
          <div className="p-2 border-b border-line flex items-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 text-muted2 shrink-0 animate-spin" /> : <Search className="w-4 h-4 text-muted2 shrink-0" />}
            <input
              autoFocus
              data-testid={`${testid}-search`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full text-sm outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {list.length === 0 ? (
              <p className="text-sm text-muted2 px-3 py-2">{loading ? "Searching…" : `No matches${q ? ` for “${q}”` : ""}.`}</p>
            ) : list.map((o) => {
              const active = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  data-testid={`${testid}-option`}
                  onClick={() => { onChange(o.value, o); setPicked(o); setOpen(false); setQ(""); }}
                  className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors ${active ? "bg-clinical/10 text-clinical font-semibold" : "text-ink hover:bg-line/50"}`}
                >
                  <span className="truncate">{o.label}{o.sublabel ? <span className="text-muted2 font-normal"> — {o.sublabel}</span> : null}</span>
                  {active && <Check className="w-4 h-4 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
